import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import { movieApi, UserPreferences, Genre, Director, Actor, handleApiError } from '../services/api';

const Profile: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const DEFAULT_WEIGHTS = { content: 40, script: 20, video: 25, audio: 15 };

  const [preferences, setPreferences] = useState<UserPreferences>({
    favorite_genres: [],
    favorite_directors: [],
    favorite_actors: [],
    min_rating: 0,
    max_runtime: undefined,
    preferred_decades: []
  });
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [availableGenres, setAvailableGenres] = useState<Genre[]>([]);
  const [availableDirectors, setAvailableDirectors] = useState<Director[]>([]);
  const [availableActors, setAvailableActors] = useState<Actor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      // Fetch reference data first (these should work)
      const [genresResponse, directorsResponse, actorsResponse] = await Promise.all([
        movieApi.getGenres(),
        movieApi.getDirectors(),
        movieApi.getActors()
      ]);

      setAvailableGenres(genresResponse.genres);
      setAvailableDirectors(directorsResponse.directors);
      setAvailableActors(actorsResponse.actors);

      // Fetch user preferences separately (might fail if auth issues)
      try {
        const prefsResponse = await movieApi.getUserPreferences();
        setPreferences(prefsResponse.preferences);
        const p = prefsResponse.preferences;
        setWeights({
          content: Math.round((p.weight_content ?? 0.40) * 100),
          script: Math.round((p.weight_script ?? 0.20) * 100),
          video: Math.round((p.weight_video ?? 0.25) * 100),
          audio: Math.round((p.weight_audio ?? 0.15) * 100),
        });
      } catch (prefsError) {
        console.error('Failed to fetch user preferences:', handleApiError(prefsError));
        // Keep default empty preferences if this fails
      }

    } catch (error) {
      console.error('Failed to fetch data:', handleApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const weightsTotal = weights.content + weights.script + weights.video + weights.audio;
  const weightsValid = weightsTotal === 100;

  const handleSave = async () => {
    if (!weightsValid) return;
    setIsSaving(true);
    setMessage(null);

    try {
      await movieApi.updateUserPreferences({
        ...preferences,
        weight_content: weights.content / 100,
        weight_script: weights.script / 100,
        weight_video: weights.video / 100,
        weight_audio: weights.audio / 100,
      });
      setMessage({ type: 'success', text: 'Preferences saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: handleApiError(error) });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenreToggle = (genreName: string) => {
    setPreferences(prev => ({
      ...prev,
      favorite_genres: prev.favorite_genres.includes(genreName)
        ? prev.favorite_genres.filter(g => g !== genreName)
        : [...prev.favorite_genres, genreName]
    }));
  };

  const handleDirectorToggle = (directorName: string) => {
    setPreferences(prev => ({
      ...prev,
      favorite_directors: prev.favorite_directors.includes(directorName)
        ? prev.favorite_directors.filter(d => d !== directorName)
        : [...prev.favorite_directors, directorName]
    }));
  };

  const handleActorToggle = (actorName: string) => {
    setPreferences(prev => ({
      ...prev,
      favorite_actors: prev.favorite_actors.includes(actorName)
        ? prev.favorite_actors.filter(a => a !== actorName)
        : [...prev.favorite_actors, actorName]
    }));
  };

  const decades = ['1970s', '1980s', '1990s', '2000s', '2010s', '2020s'];

  const handleDecadeToggle = (decade: string) => {
    setPreferences(prev => ({
      ...prev,
      preferred_decades: prev.preferred_decades.includes(decade)
        ? prev.preferred_decades.filter(d => d !== decade)
        : [...prev.preferred_decades, decade]
    }));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h1>
            <p className="text-gray-600">Please sign in to view your profile and preferences.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Profile Header */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                {user?.username?.[0]?.toUpperCase()}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{user?.username}</h1>
                <p className="text-gray-600">{user?.email}</p>
                {user?.first_name && user?.last_name && (
                  <p className="text-gray-600">{user.first_name} {user.last_name}</p>
                )}
              </div>
            </div>
          </div>

          {/* Preferences Form */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Movie Preferences</h2>
              <button
                onClick={handleSave}
                disabled={isSaving || !weightsValid}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>

            {message && (
              <div className={`mb-6 p-4 rounded-lg ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                {message.text}
              </div>
            )}

            <div className="space-y-8">
              {/* Favorite Genres */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Favorite Genres</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {availableGenres.slice(0, 20).map(genre => (
                    <button
                      key={genre.name}
                      onClick={() => handleGenreToggle(genre.name)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        preferences.favorite_genres.includes(genre.name)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {genre.name} ({genre.movie_count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Favorite Directors */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Favorite Directors</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {availableDirectors.slice(0, 20).map(director => (
                    <button
                      key={director.name}
                      onClick={() => handleDirectorToggle(director.name)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        preferences.favorite_directors.includes(director.name)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {director.name} ({director.movie_count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Favorite Actors */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Favorite Actors</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {availableActors.slice(0, 20).map(actor => (
                    <button
                      key={actor.name}
                      onClick={() => handleActorToggle(actor.name)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        preferences.favorite_actors.includes(actor.name)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {actor.name} ({actor.movie_count})
                    </button>
                  ))}
                </div>
              </div>

              {/* Preferred Decades */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Preferred Decades</h3>
                <div className="flex flex-wrap gap-2">
                  {decades.map(decade => (
                    <button
                      key={decade}
                      onClick={() => handleDecadeToggle(decade)}
                      className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                        preferences.preferred_decades.includes(decade)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {decade}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rating and Runtime Preferences */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="min_rating" className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Rating (IMDb)
                  </label>
                  <input
                    type="number"
                    id="min_rating"
                    min="0"
                    max="10"
                    step="0.1"
                    value={preferences.min_rating}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      min_rating: parseFloat(e.target.value) || 0
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="max_runtime" className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Runtime (minutes)
                  </label>
                  <input
                    type="number"
                    id="max_runtime"
                    min="60"
                    max="300"
                    value={preferences.max_runtime || ''}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      max_runtime: e.target.value ? parseInt(e.target.value) : undefined
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="No limit"
                  />
                </div>
              </div>

              {/* Recommendation Weights */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">Recommendation Weights</h3>
                  <button
                    type="button"
                    onClick={() => setWeights(DEFAULT_WEIGHTS)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Reset to defaults
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Control how much each signal contributes to your recommendations. Weights must sum to 100%.
                </p>
                <div className="space-y-4">
                  {(
                    [
                      { key: 'content', label: 'Content Features', description: 'Genres, actors, directors, decade, rating' },
                      { key: 'script', label: 'Script Similarity', description: 'Word2Vec dialogue and screenplay analysis' },
                      { key: 'video', label: 'Visual Style', description: 'CLIP trailer visual embeddings' },
                      { key: 'audio', label: 'Audio Mood', description: 'wav2vec2 soundtrack and sound design' },
                    ] as { key: keyof typeof weights; label: string; description: string }[]
                  ).map(({ key, label, description }) => (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <span className="text-sm font-medium text-gray-700">{label}</span>
                          <span className="ml-2 text-xs text-gray-400">{description}</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-900 w-10 text-right">
                          {weights[key]}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={weights[key]}
                        onChange={(e) => setWeights(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                    </div>
                  ))}
                </div>
                <div className={`mt-3 flex items-center justify-between text-sm rounded-lg px-3 py-2 ${
                  weightsValid ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                }`}>
                  <span>Total: {weightsTotal}%</span>
                  {!weightsValid && (
                    <span className="font-medium">Weights must sum to 100% before saving</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;